import { useState, useEffect } from "react";
import { Plus, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/authContext";
import { useInstitutionMutations, useInstitutionPositionTypeMutations } from "../hooks";
import { getAllPositionTypes } from "@/Services/institutions.service";
import type { Institution, InstitutionPositionType, InstitutionScope } from "../types";

const SCOPES: { key: InstitutionScope; label: string }[] = [
  { key: "national", label: "Nacional" },
  { key: "district", label: "Distrito" },
  { key: "region", label: "Región" },
];

interface Props {
  institution: Institution;
}

export function InstitutionSettingsTab({ institution }: Props) {
  const { user: authUser } = useAuth();
  const { updateInstitution, isSubmitting: savingInfo } = useInstitutionMutations(institution.id);
  const { create, update } = useInstitutionPositionTypeMutations(institution.id);

  // ── Info form ─────────────────────────────────────────────────────────────
  const [name, setName] = useState(institution.name);
  const [description, setDescription] = useState(institution.description ?? "");
  const infoChanged = name.trim() !== institution.name || description.trim() !== (institution.description ?? "");

  useEffect(() => {
    setName(institution.name);
    setDescription(institution.description ?? "");
  }, [institution.id]);

  async function handleSaveInfo() {
    if (!authUser) return;
    await updateInstitution(
      { name: name.trim(), description: description.trim() || null },
      authUser.id,
    );
  }

  // ── Position types ────────────────────────────────────────────────────────
  const [positionTypes, setPositionTypes] = useState<InstitutionPositionType[]>([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const [addingScope, setAddingScope] = useState<InstitutionScope | null>(null);
  const [newName, setNewName] = useState("");
  const [savingNew, setSavingNew] = useState(false);

  async function loadPositions() {
    setLoadingPositions(true);
    try {
      const data = await getAllPositionTypes(institution.id);
      setPositionTypes(data);
    } finally {
      setLoadingPositions(false);
    }
  }

  useEffect(() => { loadPositions(); }, [institution.id]);

  async function handleToggle(pt: InstitutionPositionType) {
    setTogglingId(pt.id);
    try {
      const updated = await update(pt.id, { is_active: !pt.is_active });
      setPositionTypes((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } finally {
      setTogglingId(null);
    }
  }

  async function handleMove(pt: InstitutionPositionType, direction: "up" | "down") {
    const scopeItems = positionTypes
      .filter((p) => p.scope === pt.scope)
      .sort((a, b) => a.sort_order - b.sort_order);
    const idx = scopeItems.findIndex((p) => p.id === pt.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= scopeItems.length) return;

    const peer = scopeItems[swapIdx];
    setReorderingId(pt.id);
    try {
      const [a, b] = await Promise.all([
        update(pt.id, { sort_order: peer.sort_order }),
        update(peer.id, { sort_order: pt.sort_order }),
      ]);
      setPositionTypes((prev) =>
        prev.map((p) => (p.id === a.id ? a : p.id === b.id ? b : p)),
      );
    } finally {
      setReorderingId(null);
    }
  }

  async function handleCreate(scope: InstitutionScope) {
    if (!authUser || !newName.trim()) return;
    const scopeItems = positionTypes.filter((p) => p.scope === scope);
    const maxOrder = scopeItems.reduce((m, p) => Math.max(m, p.sort_order), 0);
    setSavingNew(true);
    try {
      const created = await create({ name: newName.trim(), scope, sort_order: maxOrder + 1 }, authUser.id);
      setPositionTypes((prev) => [...prev, created]);
      setNewName("");
      setAddingScope(null);
    } finally {
      setSavingNew(false);
    }
  }

  const inputCls =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

  return (
    <div className="space-y-8">
      {/* ── Información general ── */}
      <div className="rounded-lg border border-border bg-card p-6">
        <h3 className="mb-4 text-base font-semibold">Información general</h3>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Nombre</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={savingInfo}
              className={inputCls}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Descripción</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={savingInfo}
              rows={3}
              className={inputCls}
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSaveInfo}
              disabled={savingInfo || !infoChanged || !name.trim()}
            >
              {savingInfo && <Loader2 className="mr-2 size-4 animate-spin" />}
              Guardar cambios
            </Button>
          </div>
        </div>
      </div>

      {/* ── Tipos de cargo ── */}
      <div className="rounded-lg border border-border bg-card p-6">
        <h3 className="mb-6 text-base font-semibold">Tipos de cargo</h3>
        {loadingPositions ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-8">
            {SCOPES.map(({ key, label }) => {
              const items = positionTypes
                .filter((p) => p.scope === key)
                .sort((a, b) => a.sort_order - b.sort_order);
              const isAddingHere = addingScope === key;

              return (
                <div key={key} className="space-y-3">
                  {/* Scope header */}
                  <div className="flex items-center gap-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {label}
                    </h4>
                    <div className="h-px flex-1 bg-border" />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1 text-xs"
                      onClick={() => {
                        setAddingScope(isAddingHere ? null : key);
                        setNewName("");
                      }}
                    >
                      <Plus className="size-3" />
                      Agregar
                    </Button>
                  </div>

                  {/* Add row */}
                  {isAddingHere && (
                    <div className="flex items-center gap-2 rounded-md border border-dashed border-primary/50 bg-primary/5 px-3 py-2">
                      <input
                        autoFocus
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleCreate(key);
                          if (e.key === "Escape") { setAddingScope(null); setNewName(""); }
                        }}
                        placeholder="Nombre del cargo..."
                        disabled={savingNew}
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      />
                      <Button size="sm" onClick={() => handleCreate(key)} disabled={savingNew || !newName.trim()}>
                        {savingNew ? <Loader2 className="size-3 animate-spin" /> : "Crear"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setAddingScope(null); setNewName(""); }}>
                        Cancelar
                      </Button>
                    </div>
                  )}

                  {/* Position rows */}
                  {items.length === 0 ? (
                    <p className="py-2 text-center text-xs text-muted-foreground">
                      Sin cargos configurados.
                    </p>
                  ) : (
                    <div className="divide-y divide-border rounded-md border border-border">
                      {items.map((pt, idx) => {
                        const isToggling = togglingId === pt.id;
                        const isReordering = reorderingId === pt.id;
                        return (
                          <div
                            key={pt.id}
                            className={`flex items-center gap-3 px-3 py-2.5 ${!pt.is_active ? "opacity-50" : ""}`}
                          >
                            <span className="flex-1 text-sm">{pt.name}</span>
                            {/* Reorder */}
                            <div className="flex shrink-0 gap-0.5">
                              <button
                                onClick={() => handleMove(pt, "up")}
                                disabled={idx === 0 || !!reorderingId}
                                className="rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-30"
                                title="Subir"
                              >
                                {isReordering ? (
                                  <Loader2 className="size-3 animate-spin" />
                                ) : (
                                  <span className="text-xs leading-none">▲</span>
                                )}
                              </button>
                              <button
                                onClick={() => handleMove(pt, "down")}
                                disabled={idx === items.length - 1 || !!reorderingId}
                                className="rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-30"
                                title="Bajar"
                              >
                                <span className="text-xs leading-none">▼</span>
                              </button>
                            </div>
                            {/* Toggle active */}
                            <button
                              onClick={() => handleToggle(pt)}
                              disabled={!!togglingId}
                              title={pt.is_active ? "Desactivar" : "Activar"}
                              className="rounded p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
                            >
                              {isToggling ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : pt.is_active ? (
                                <Eye className="size-4" />
                              ) : (
                                <EyeOff className="size-4" />
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
